import React, { useState } from 'react';
import { Alert, Card, Spin, Typography } from 'antd';
import { LineChartOutlined } from '@ant-design/icons';
import EChart from '../../../components/charts/EChart';
import BedMetricSelector from './BedMetricSelector';
import { useBedTimeseries } from '../hooks/useBedTimeseries';
import { useBedEnvironmentChart } from '../hooks/useBedEnvironmentChart';
import type { BedMetricKey } from '../constants';
import WeatherGapDialog from '../../../components/weather/WeatherGapDialog';
import { useTranslation } from 'react-i18next';
import { formatDate as formatLocaleDate } from '../../../i18n';

const { Text } = Typography;

interface BedEnvironmentCardProps {
  bedId: string;
  saveId: string;
}

const BedEnvironmentCard: React.FC<BedEnvironmentCardProps> = ({ bedId, saveId }) => {
  const { t } = useTranslation('beds');
  const [selectedMetric, setSelectedMetric] = useState<BedMetricKey>('moisturePct');
  const [gapDismissed, setGapDismissed] = useState(false);

  const {
    timeSeries,
    totalDays,
    isLoading,
    hasSession,
    wateringAutomation,
    weatherGap,
    refetch,
  } = useBedTimeseries(bedId, saveId);

  const chartOption = useBedEnvironmentChart({
    timeSeries,
    selectedMetric,
    wateringAutomation,
  });
  const latestWateringOn = [...(wateringAutomation?.events ?? [])]
    .reverse()
    .find(event => event.type === 'watering_on');
  const moistureSinceEnable = latestWateringOn
    ? timeSeries
        .filter(point => point.date >= latestWateringOn.date)
        .map(point => point.moisturePct)
        .filter((value): value is number => Number.isFinite(value))
    : [];
  const minMoistureSinceEnable = moistureSinceEnable.length > 0
    ? Math.min(...moistureSinceEnable)
    : null;
  const activationPct = wateringAutomation?.guide.activationPct;
  const automationDidNotTrigger = wateringAutomation?.enabled
    && minMoistureSinceEnable != null
    && activationPct != null
    && minMoistureSinceEnable > activationPct;
  const formatDate = (date: string) => formatLocaleDate(String(date).slice(0, 10), 'short');

  return (
    <Card
      title={
        <span>
          <LineChartOutlined style={{ marginRight: 8 }} />
          {t('environment.title')}
        </span>
      }
      size="small"
      style={{ marginBottom: 16 }}
    >
      <BedMetricSelector
        selectedMetric={selectedMetric}
        onSelectMetric={setSelectedMetric}
      />

      {selectedMetric === 'moisturePct'
        && wateringAutomation?.enabled
        && latestWateringOn
        && activationPct != null && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12, marginBottom: 12 }}
          message={t('environment.autoWateringEnabledOn', { date: formatDate(latestWateringOn.date) })}
          description={automationDidNotTrigger
            ? t('environment.autoWateringNotTriggered', { activation: activationPct.toFixed(0), min: minMoistureSinceEnable?.toFixed(1) })
            : t('environment.autoWateringHint', { activation: activationPct.toFixed(0), target: wateringAutomation?.guide.targetPct.toFixed(0) })}
        />
      )}

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : !hasSession ? (
        <Text type="secondary">
          {t('environment.noSession')}
        </Text>
      ) : totalDays === 0 ? (
        <Text type="secondary">
          {t('environment.noData')}
        </Text>
      ) : (
        <EChart option={chartOption} height={300} />
      )}

      <WeatherGapDialog
        open={Boolean(weatherGap) && !gapDismissed}
        gap={weatherGap}
        onClose={() => setGapDismissed(true)}
        onExtended={() => {
          setGapDismissed(false);
          refetch?.();
        }}
      />
    </Card>
  );
};

export default BedEnvironmentCard;
